import { useState } from "react";
import { MailWarning, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/hooks/useAuth";
import { sendEmailVerification } from "@/api/emailVerification";
import { useToast } from "@/hooks/useToast";

/**
 * Shows a top-of-page reminder when the current user has not yet proved
 * ownership of their email. Disappears once ``email_verified`` flips true
 * or the user dismisses it for the session.
 */
export function EmailVerificationBanner() {
  const { user, refreshUser } = useAuth();
  const { addToast } = useToast();
  const [dismissed, setDismissed] = useState(false);
  const [isSending, setIsSending] = useState(false);

  if (!user) return null;
  if (user.email_verified) return null;
  if (dismissed) return null;

  const resend = async () => {
    setIsSending(true);
    try {
      const res = await sendEmailVerification();
      addToast("success", res.message || "Verification email sent");
      // In dev, the token is surfaced so we can verify without an SMTP server.
      if (res.dev_verification_token) {
        // eslint-disable-next-line no-console
        console.info("[dev] verification URL:", res.verification_url);
      }
      await refreshUser();
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: { detail?: string } };
      };
      if (axiosErr.response?.status === 429) {
        addToast(
          "info",
          axiosErr.response.data?.detail ||
            "You can request another verification email in a minute."
        );
      } else {
        addToast(
          "error",
          axiosErr.response?.data?.detail ||
            "Could not send verification. Please try again."
        );
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 mb-4">
      <MailWarning className="h-5 w-5 text-warning shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-text-primary">
          Verify your email address
        </p>
        <p className="text-xs text-text-muted mt-0.5">
          We sent a verification link to <strong>{user.email}</strong>. Some
          features stay limited until it's confirmed.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={resend} isLoading={isSending}>
          Resend
        </Button>
        <button
          aria-label="Dismiss"
          className="text-text-muted hover:text-text-primary"
          onClick={() => setDismissed(true)}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
